import React, { useState, useEffect } from 'react';
import '../styles/onboard.css';
import { auth } from '../context/auth.ts';
import { supabase } from '../integrations/client.ts';
import OnboardLeftSection from '../components/OnboardLeftSection';
import { isValidEmail, getEmailValidationError } from '../utils/validation';

function SignUp({ onClose, onSwitchToSignIn, onNavigateToVerifyEmail }) {
    const [step, setStep] = useState('tiers'); // 'tiers' or 'form'
    const [selectedTier, setSelectedTier] = useState(null);
    const [tiers, setTiers] = useState([]);
    const [loadingTiers, setLoadingTiers] = useState(true);
    const [conversationStage, setConversationStage] = useState('welcome'); // 'welcome', 'explanation', 'acknowledgment'
    const [chatComplete, setChatComplete] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        confirmPassword: '',
        name: ''
    });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchSubscriptionTiers();
    }, []);

    const fetchSubscriptionTiers = async () => {
        try {
            const { data, error } = await supabase
                .from('subscription_tiers')
                .select('*')
                .order('price_amount', { ascending: true });


            if (error) {
                setError(`Failed to load subscription plans: ${error.message}`);
                setLoadingTiers(false);
                return;
            }

            if (data && data.length > 0) {
                // Transform the data to match the expected format
                const formattedTiers = data.map(tier => ({
                    id: tier.id,
                    title: tier.title,
                    description: tier.description,
                    price: tier.price_amount === 0 ? '$0' : `$${tier.price_amount}`,
                    period: tier.price_period,
                    features: tier.features,
                    popular: tier.is_popular
                }));
                setTiers(formattedTiers);
            } else {
                setError('No subscription plans available. Please contact support.');
            }
        } catch (err) {
            setError(`Failed to load subscription plans: ${err.message}`);
        } finally {
            setLoadingTiers(false);
        }
    };

    const handleTierSelect = (tierId) => {
        setSelectedTier(tierId);
        setStep('form');
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error when user starts typing
        if (error) setError('');
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        const { email, password, confirmPassword, name } = formData;

        // Validation
        if (!email || !password || !confirmPassword || !name) {
            setError('Please fill in all fields');
            setLoading(false);
            return;
        }

        // Email validation
        const emailValidationError = getEmailValidationError(email);
        if (emailValidationError) {
            setError(emailValidationError);
            setLoading(false);
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            setLoading(false);
            return;
        }

        // Strong password validation
        if (password.length < 10) {
            setError('Password must be at least 10 characters long');
            setLoading(false);
            return;
        }

        const hasUpperCase = /[A-Z]/.test(password);
        const hasLowerCase = /[a-z]/.test(password);
        const hasNumber = /[0-9]/.test(password);
        const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);

        if (!hasUpperCase) {
            setError('Password must contain at least one uppercase letter');
            setLoading(false);
            return;
        }

        if (!hasLowerCase) {
            setError('Password must contain at least one lowercase letter');
            setLoading(false);
            return;
        }

        if (!hasNumber) {
            setError('Password must contain at least one number');
            setLoading(false);
            return;
        }

        if (!hasSpecialChar) {
            setError('Password must contain at least one special character (!@#$%^&*()_+-=[]{};\':"|,.<>/?)');
            setLoading(false);
            return;
        }

        try {
            // SECURITY: Do NOT pass subscription_type to metadata
            // All users start as 'free' and only upgrade via Stripe payment
            const { data, error } = await auth.signUp(email, password, {
                name: name,
                theme_preference: 'light'
                // subscription_type is intentionally omitted - always defaults to 'free'
            });

            if (error) {
                if (error.message.includes('User already registered')) {
                    setError('An account with this email already exists. Please sign in instead.');
                } else {
                    setError(error.message || 'Sign up failed. Please try again.');
                }
                setLoading(false);
                return;
            }

            if (data.user) {
                // Profile is automatically created by database trigger as 'free'
                // No need to manually create it here

                // Supabase will automatically send the verification email
                console.log('Signup successful. Verification email will be sent by Supabase.');

                // All users start with a free account
                // They can upgrade after verification and sign-in
                console.log('User signed up for free tier. They can upgrade after verifying email and signing in.');

                // Immediately redirect to verify email page
                console.log('Redirecting to verify email page...');

                setLoading(false);

                // Use setTimeout to ensure loading state is updated before navigation
                setTimeout(() => {
                    if (onNavigateToVerifyEmail) {
                        onNavigateToVerifyEmail();
                    }
                }, 100);
            } else {
                setError('Sign up failed. Please check your email for confirmation instructions.');
                setLoading(false);
            }
        } catch (err) {
            setError('An unexpected error occurred. Please try again.');
        }

        setLoading(false);
    };

    const handleBackToTiers = () => {
        setStep('tiers');
        setSelectedTier(null);
        setFormData({
            email: '',
            password: '',
            confirmPassword: '',
            name: ''
        });
        setError('');
    };

    const handleSignInClick = () => {
        if (onSwitchToSignIn) {
            onSwitchToSignIn();
        }
    };

    const handleYesClick = () => {
        setConversationStage('explanation');
    };

    const handleNoClick = () => {
        setConversationStage('acknowledgment');
    };

    const handleChatComplete = () => {
        setChatComplete(true);
    };

    const handleLearnMoreClick = () => {
        // You can add a URL or action here - for now, just log it
        window.open('https://docs.teachingorb.com', '_blank');
    };

    if (step === 'form') {
        return (
            <div className="onboard-container signin-container">
                <div className="signin-layout">
                    {/* Left Section */}
                    <OnboardLeftSection
                        onClose={handleBackToTiers}
                        loading={loading}
                        mode="signupForm"
                    />

                    {/* Right Section */}
                    <div className="signin-content">
                        {/* Mobile Back Button */}
                        {/* <button
                            className="back-button-bottom"
                            onClick={handleBackToTiers}
                            disabled={loading}
                            aria-label="Go back"
                        >
                            <span className="back-button-text">Back</span>
                            <span className="back-button-icon">&larr;</span>
                        </button> */}

                        <h1>Create Your Account</h1>
                        <p className="signup-subtitle">You're creating a free account. Upgrade to Premium anytime after signing in.</p>

                        <form className="signup-form" onSubmit={handleFormSubmit}>
                        {error && (
                            <div className="error-message" style={{
                                color: '#e74c3c',
                                backgroundColor: '#fdf2f2',
                                padding: '10px',
                                borderRadius: '4px',
                                marginBottom: '15px',
                                border: '1px solid #fecaca'
                            }}>
                                {error}
                            </div>
                        )}

                        <div className="form-group">
                            <label htmlFor="name">Full Name</label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                placeholder="Enter your full name"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="email">Email</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                placeholder="Enter your email"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="password">Password</label>
                            <input
                                type="password"
                                id="password"
                                name="password"
                                value={formData.password}
                                onChange={handleInputChange}
                                placeholder="Create a strong password"
                                required
                            />
                            <small style={{ color: '#666', fontSize: '11px', marginTop: '5px', display: 'block' }}>
                                Must be at least 10 characters with uppercase, lowercase, number, and special character
                            </small>
                        </div>

                        <div className="form-group">
                            <label htmlFor="confirmPassword">Confirm Password</label>
                            <input
                                type="password"
                                id="confirmPassword"
                                name="confirmPassword"
                                value={formData.confirmPassword}
                                onChange={handleInputChange}
                                placeholder="Confirm your password"
                                required
                            />
                        </div>

                            <button type="submit" className="signin-btn" disabled={loading}>
                                {loading ? 'Creating Account...' : 'Create Account'}
                            </button>
                        </form>

                        <div className="signin-footer">
                            <p>Already have an account? <span className="signup-link" onClick={handleSignInClick}>Sign in here</span></p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="onboard-container signin-container">
            <div className="signin-layout">
                {/* Left Section */}
                <OnboardLeftSection
                    onClose={onClose}
                    loading={loadingTiers}
                    mode="signup"
                    signUpStage={conversationStage}
                    onYesClick={handleYesClick}
                    onNoClick={handleNoClick}
                    onChatStreamComplete={handleChatComplete}
                    showLearnMore={chatComplete}
                    onLearnMoreClick={handleLearnMoreClick}
                />

                {/* Right Section */}
                <div className="signin-content" style={{ maxWidth: '1400px', width: '100%' }}>
                    {/* Mobile Back Button */}
                    <button
                        className="back-button-bottom"
                        onClick={onClose}
                        disabled={loadingTiers}
                        aria-label="Go back"
                    >
                        <span className="back-button-text">Back</span>
                        <span className="back-button-icon">&larr;</span>
                    </button>

                    <h1>Choose Your Learning Plan</h1>
                    <div className="tier-info-banner">
                        <p style={{ margin: 0, fontSize: '14px', color: 'var(--bg-secondary)' }}>
                            All new accounts start as <strong>Free</strong>. You can upgrade to Premium after signing up.
                        </p>
                    </div>

                {loadingTiers ? (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        <p>Loading subscription plans...</p>
                    </div>
                ) : error && tiers.length === 0 ? (
                    <div className="error-message" style={{
                        color: '#e74c3c',
                        backgroundColor: '#fdf2f2',
                        padding: '20px',
                        borderRadius: '4px',
                        marginBottom: '15px',
                        border: '1px solid #fecaca',
                        textAlign: 'center'
                    }}>
                        {error}
                    </div>
                    ) : (
                        <div className="tiers-container">
                            {tiers.map((tier) => (
                                <div
                                    key={tier.id}
                                    className={`tier-card ${tier.popular ? 'popular' : ''}`}
                                    onClick={() => handleTierSelect(tier.id)}
                                >
                                    {tier.popular && <div className="popular-badge">Most Popular</div>}

                                    <div className="tier-content">
                                        <div className="tier-header">
                                            <h3 className="tier-title">{tier.title}</h3>
                                            <div className="tier-price">
                                                <span className="price">{tier.price}</span>
                                                <span className="period">{tier.period}</span>
                                            </div>
                                        </div>

                                        <div className="tier-features">
                                            <ul>
                                                {tier.features.map((feature, index) => (
                                                    <li key={index}>{feature}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>

                                    <div className="tier-footer">
                                        <button className="select-tier-btn">
                                            {tier.price === '$0' ? 'Get started free' : 'Choose plan'}
                                        </button>
                                        <p className="tier-disclaimer">
                                            {tier.price !== '$0' ? 'hands-on projects availability depends on the course generated' : ''}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="signin-footer">
                        <p>Already have an account? <span className="signup-link" onClick={handleSignInClick}>Sign in here</span></p>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default SignUp;